// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@aviary/components";
import { useProductAvailabilityLabel } from "@aviary/hooks";
import { l } from "@aviary/locales/i18n";

import { AvailabilityPill } from "./AvailabilityPill";
import { OnlyOnFullscriptBadge } from "./OnlyOnFullscriptBadge";
import type { ProductImageProps } from "./ProductImage.types";
import { QualityPartnerBadge } from "./QualityPartnerBadge";

import * as styles from "./ProductImage.styles";

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/molecules/ProductImage/
 */
const ProductImage = ({
  size = "medium",
  name,
  image,
  availabilityStatus,
  expectedReturnDate,
  isOnlyOnFS = false,
  isPaddingless,
  count,
  qualityPartner = null,
  disableLazyLoading = false,
  isLoaded = true,
  onLoad,
}: ProductImageProps) => {
  const { t } = useTranslation();
  const { availabilityLabelText } = useProductAvailabilityLabel(availabilityStatus);

  const [hasLoadError, setHasLoadError] = useState(false);

  const handleImageError = e => {
    e.stopPropagation();
    setHasLoadError(true);
  };

  const renderImgAltText = () => {
    const countText = count > 0 ? ` ${t(l.aviary.productImage.quantity, { count })}` : "";
    const availabilityLabel = availabilityLabelText ? ` ${availabilityLabelText}.` : "";
    const isFSOnlyText = isOnlyOnFS ? ` ${t(l.aviary.productImage.onlyOnFS)}.` : "";

    return `${t(l.aviary.productImage.altText, {
      name,
    })}${isFSOnlyText}${countText}${availabilityLabel}`;
  };

  const renderBadgeSize = () => {
    if (size === "xxsmall" || size === "xsmall" || size === "small") {
      return "xsmall";
    }

    if (size === "xxlarge") {
      return "large";
    }

    return "small";
  };

  return (
    // className needed for ProductImageGallery
    <div className="aviary-product-image" css={[styles.wrapper, styles.productImg[size]]}>
      <img
        src={hasLoadError ? "/assets/noimage/fallback-product-image.png" : image}
        alt={renderImgAltText()}
        title={name}
        css={[
          styles.productImg.base,
          availabilityLabelText && styles.productImg.isFaded,
          isPaddingless && styles.productImg.isPaddingless,
        ]}
        onError={handleImageError}
        loading={disableLazyLoading ? null : "lazy"}
        onLoad={onLoad}
        data-testid="product-image-molecule"
      />
      {!!isLoaded && (
        <>
          <AvailabilityPill
            availabilityStatus={availabilityStatus}
            expectedReturnDate={expectedReturnDate}
            size={size}
            css={styles.availabilityPill}
            aria-hidden={size !== "xxsmall" && size !== "xsmall"}
          />
          {!!isOnlyOnFS && (
            <OnlyOnFullscriptBadge
              size={renderBadgeSize()}
              isTooltipHidden={size === "xxsmall" || size === "xsmall"}
              layoutStyles={[
                styles.onlyOnFSPill.base,
                (size === "xxsmall" || size === "xsmall") && styles.onlyOnFSPill.outsideCorner,
                size === "small" && styles.onlyOnFSPill.outsideCornerTooltip,
                size === "xxlarge" && styles.onlyOnFSPill.pill,
              ]}
            />
          )}

          {count > 0 && size !== "xxlarge" && <Badge isColor="success" count={count} />}

          {!!qualityPartner && (
            <QualityPartnerBadge
              qualityPartner={qualityPartner}
              size={renderBadgeSize()}
              layoutStyles={[
                styles.qualityPartnerBadge.base,
                size === "xxlarge" && styles.qualityPartnerBadge.large,
                (size === "xxsmall" || size === "xsmall" || size === "small") &&
                  styles.qualityPartnerBadge.outsideCorner,
              ]}
            />
          )}
        </>
      )}
    </div>
  );
};

export { ProductImage };
